import { CheckoutPageKeys } from "@app/checkout/constants/CheckoutPageKeys";
import { DebugDialog } from "@app/common/components/DebugDialog";
import { LanguageAndCurrencySelector } from "@app/common/components/LanguageAndCurrencySelector";
import { PromoCodeModal } from "@app/common/components/PromoCodeModal";
import { StorageKeys as CommonStorageKeys } from "@app/common/constants/StorageKeys";
import { HotelchampLogo } from "@app/componentLibrary/icon/HotelchampLogo";
import { Button } from "@app/componentLibrary/ui/button";
import { Flex } from "@app/componentLibrary/ui/flex";
import { Tooltip, TooltipContent, TooltipTrigger } from "@app/componentLibrary/ui/tooltip";
import { BookingWhizzMemberModal } from "@app/loyalty/components/BookingWhizzMemberModal";
import { VendorLoyaltyProvider } from "@app/loyalty/constants/VendorLoyaltyProvider";
import { UserCircleIcon } from "@heroicons/react/20/solid";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { TooltipArrow, TooltipProvider } from "@radix-ui/react-tooltip";
import React, { Fragment, Ref, Suspense, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import { WidgetContentProvider } from "../context/WidgetContentContext";
import { useAppState, usePropertyContext } from "../hooks";
import { appElementsProvider } from "../services/appElements";
import { storageManager } from "../services/storageManager";
import { BookingCheckWarning } from "./BookingCheckWarning";
import { NavigateBack } from "./NavigateBack";
import { Outlet } from "./router/Outlet";
import { useRouter } from "./router/RouterContext";

export interface IWidgetProps {
    style?: React.CSSProperties;
    className?: string;
    onCloseClick?: () => void;
    onTransitionEnd?: () => void;
}

const baseStyle: React.CSSProperties = {
    position: "fixed",
    right: "0px",
    bottom: "0px",
    height: "100%",
    maxHeight: "936px",
    minHeight: "250px",
};

/**
 * Widget
 */
export const Widget = React.forwardRef(
    ({ onCloseClick, onTransitionEnd, className, style, ...widgetContainerProps }: IWidgetProps, ref: Ref<HTMLDivElement>) => {
        const { activeProperty } = usePropertyContext();
        const { tw } = appElementsProvider.getAppElements();
        const { t } = useTranslation(["availability", "common"]);
        const [isDebuggerActive, setIsDebuggerActive] = useState(false);
        const outletRef = useRef<any>(null);
        const widgetContentRef = useRef<HTMLDivElement>(null);
        const sessionStorage = storageManager.getGlobalSession();

        const { route } = useRouter();
        const activeStep = route.query?.c;
        const showPromoCodeButton = ![CheckoutPageKeys.CheckoutEntry.toString(), CheckoutPageKeys.CheckoutFinished.toString()].includes(
            activeStep
        );

        const { state: appState } = useAppState();

        useEffect(() => {
            // eslint-disable-next-line no-underscore-dangle
            const windowIsDebuggerActive = (window as any)?.__hc_ibe_isDebuggerActive;
            const localStorageIsDebuggerActive = localStorage.getItem("__hc_ibe_isDebuggerActive");

            let globalIsDebuggerActive: undefined | boolean;

            if (windowIsDebuggerActive !== undefined && ["true", "false"].includes(windowIsDebuggerActive)) {
                globalIsDebuggerActive = windowIsDebuggerActive === "true";
            } else if (
                windowIsDebuggerActive === undefined &&
                localStorageIsDebuggerActive !== null &&
                ["true", "false"].includes(localStorageIsDebuggerActive)
            ) {
                globalIsDebuggerActive = localStorageIsDebuggerActive === "true";
            }

            if (globalIsDebuggerActive !== undefined) {
                sessionStorage.set(CommonStorageKeys.DebuggerActive, globalIsDebuggerActive);

                setIsDebuggerActive(globalIsDebuggerActive);
            } else {
                sessionStorage.get(CommonStorageKeys.DebuggerActive).then((restoredIsDebuggerActive) => {
                    const nextIsDebuggerActive = !!restoredIsDebuggerActive;

                    if (isDebuggerActive !== nextIsDebuggerActive) {
                        setIsDebuggerActive(nextIsDebuggerActive);
                    }
                });
            }
        }, []);

        return (
            <div
                data-testid="widget"
                onTransitionEnd={onTransitionEnd}
                {...widgetContainerProps}
                style={{ ...baseStyle, ...(style || {}) }}
                ref={ref}
                className={tw!(twMerge(className, "max-w-lg w-full sm:p-5 md:pb-14 overflow-hidden"))}
            >
                <Flex justifyContent="end" className="w-full h-full">
                    {/* @important - "relative" is required for popover backdrops to only put the backdrop on top of the widget instead of over the shadow DOM element */}
                    <div
                        id="hc-widget-content"
                        ref={widgetContentRef}
                        className={tw!(twMerge("relative max-w-lg w-full h-full ease-in-out"))}
                    >
                        <WidgetContentProvider widgetContentRef={widgetContentRef}>
                            <Flex
                                flexDirection="col"
                                justifyContent="between"
                                className={tw!("sm:rounded-xl w-full h-full shadow-lg bg-white pt-7 relative")}
                            >
                                <Flex content="center" justifyContent="between" className="px-5 flex-none py-0 pb-1">
                                    <Fragment>
                                        <NavigateBack />
                                        <Flex justifyContent="end" className="gap-x-4">
                                            {/* <ExpandView /> */}
                                            <PromoCodeModal />
                                            <LanguageAndCurrencySelector />
                                            <TooltipProvider>
                                                <Tooltip>
                                                    {isDebuggerActive && (
                                                        <TooltipTrigger>
                                                            <DebugDialog />
                                                        </TooltipTrigger>
                                                    )}
                                                </Tooltip>
                                            </TooltipProvider>

                                            {/* Load BookingWhizz loyalty modal if required */}
                                            {activeProperty?.vendor_loyalty_provider === VendorLoyaltyProvider.BookingWhizz && (
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger>
                                                            <BookingWhizzMemberModal
                                                                triggerButton={
                                                                    <Button
                                                                        data-testid="loyalty-modal-btn"
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="text-gray-500"
                                                                    >
                                                                        <UserCircleIcon width={20} />
                                                                    </Button>
                                                                }
                                                                isMainModal={true}
                                                            />
                                                        </TooltipTrigger>
                                                        <TooltipContent sideOffset={2}>
                                                            <p className="text-xs">Loyalty</p>
                                                            <TooltipArrow className="TooltipArrow" />
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            )}

                                            <Button data-testid="ibe-close-btn" size="icon" variant="outline" onClick={onCloseClick}>
                                                <XMarkIcon width={16} className="text-gray-500 cursor-pointer" />
                                            </Button>
                                        </Flex>
                                    </Fragment>
                                </Flex>
                                <BookingCheckWarning />
                                <Suspense>
                                    <Outlet />
                                </Suspense>
                            </Flex>
                        </WidgetContentProvider>
                    </div>
                </Flex>
                <Flex justifyContent="center" alignItems="center" className="text-white text-xs p-2 invisible md:visible">
                    <p className="mr-1.5 font-normal">Powered by</p>
                    <HotelchampLogo />
                </Flex>
            </div>
        );
    }
);
